import { format } from 'date-fns-tz';
import { pentagonAreaLines, pentagonAreas } from './constants';
import { utcToSite } from '../../../../datetimeUtils';

export const getPentagonAreaLines = (duvalType) =>
  pentagonAreaLines[duvalType] || pentagonAreaLines[1];

export const getPentagonAreas = (duvalType) => pentagonAreas[duvalType] || pentagonAreas[1];

export function getPolygonCentroid(points) {
  let xSum = 0;
  let ySum = 0;
  let area = 0;

  const n = points.length;

  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    const cross = points[i].x * points[j].y - points[j].x * points[i].y;
    xSum += (points[i].x + points[j].x) * cross;
    ySum += (points[i].y + points[j].y) * cross;
    area += cross;
  }

  area *= 0.5;
  const factor = 1 / (6 * area);

  return {
    x: xSum * factor,
    y: ySum * factor,
  };
}

export function getGasCentroid(gasses) {
  const gassesSum = gasses.reduce((acc, cur) => acc + cur, 0);
  const gasPoints = gasses.map((gas, i) => {
    const relGas = gas / gassesSum;
    const relGasPercentage = relGas * 100;
    const gasAngle = 90 - 72 * i;
    return {
      x: parseFloat((relGasPercentage * Math.cos((gasAngle * Math.PI) / 180) * -1).toFixed(10)),
      y: parseFloat(
        (relGasPercentage * Math.cos(((gasAngle - 90) * Math.PI) / 180) * -1).toFixed(10)
      ),
    };
  });
  return getPolygonCentroid(gasPoints);
}

export const normalizeCoordinate = (coord) => (coord / 40) * 50;

export function getCurrentPentagonArea({
  h2,
  c2h6,
  ch4,
  c2h4,
  c2h2,
  duvalType,
  pentagonWidth,
  pentagonHeight,
}) {
  const currentArea = getPentagonAreas(duvalType).find((area) => {
    const polyCorners = area.points.length;
    const polyX = area.points.map(
      (point) => pentagonWidth / 2 + pentagonWidth * (normalizeCoordinate(point[0]) / 100)
    );
    const polyY = area.points.map(
      (point) => pentagonHeight / 2 + pentagonHeight * (normalizeCoordinate(point[1]) / 100)
    );

    let j = polyCorners - 1;

    const gasses = [h2, c2h6, ch4, c2h4, c2h2];

    const centroidCoordinates = getGasCentroid(gasses);

    const x =
      pentagonWidth / 2 + pentagonWidth * (normalizeCoordinate(centroidCoordinates.x) / 100);
    const y =
      pentagonHeight / 2 + pentagonHeight * (normalizeCoordinate(centroidCoordinates.y) / 100);

    let oddNodes = false;

    for (let i = 0; i < polyCorners; i += 1) {
      if ((polyY[i] < y && polyY[j] >= y) || (polyY[j] < y && polyY[i] >= y)) {
        if (polyX[i] + ((y - polyY[i]) / (polyY[j] - polyY[i])) * (polyX[j] - polyX[i]) < x) {
          oddNodes = !oddNodes;
        }
      }
      j = i;
    }

    return oddNodes;
  });

  return currentArea?.name?.slice(0, 2) || 'Un';
}

export const tooltipLabelFormatter = (time, timezone) => {
  const dateFormat = 'yyyy-MM-dd, HH:mm:ss';
  return format(utcToSite(time, timezone), dateFormat, { timeZone: timezone });
};

export const darkenHex = (color, amount) => {
  let R = parseInt(color[1] + color[2], 16);
  let G = parseInt(color[3] + color[4], 16);
  let B = parseInt(color[5] + color[6], 16);

  R = R > amount ? R - amount : 0o0;
  G = G > amount ? G - amount : 0o0;
  B = B > amount ? B - amount : 0o0;

  const RHex = R > 16 ? R.toString(16) : `0${R.toString(16)}`;
  const BHex = B > 16 ? B.toString(16) : `0${B.toString(16)}`;
  const GHex = G > 16 ? G.toString(16) : `0${G.toString(16)}`;

  const darkenedColor = `#${RHex}${GHex}${BHex}`;
  return darkenedColor;
};
